import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
  IconButton
} from '@mui/material';
import { Close, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useSignMessageStore } from '../stores/signMessageStore';

interface SignMessageDialogProps {
  onClose?: () => void;
  onCancel?: () => void;
}

const SignMessageDialog: React.FC<SignMessageDialogProps> = ({
  onClose,
  onCancel
}) => {
  const {
    isOpen,
    title,
    steps,
    activeStep,
    message,
    loading,
    error,
    setOpen,
    reset
  } = useSignMessageStore();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setOpen(false);
      reset();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setOpen(false);
      reset();
    }
  };

  const isCompleted = activeStep >= steps.length;
  const progress = steps.length > 0 ? ((activeStep + 1) / steps.length) * 100 : 0;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          {!loading && (
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: 'grey.500' }}
            >
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Progress Bar */}
        {steps.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary" align="center">
              {isCompleted ? 'Completed' : `Step ${activeStep + 1} / ${steps.length}`}
            </Typography>
          </Box>
        )}

        {/* Stepper */}
        {steps.length > 0 && (
          <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 3 }}>
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              const hasError = error && index === activeStep;

              return (
                <Step key={step} completed={isCompleted}>
                  <StepLabel
                    error={hasError || undefined}
                    StepIconComponent={({ active, completed, error }) => {
                      if (error) {
                        return <ErrorIcon color="error" />;
                      }
                      if (completed) {
                        return <CheckCircle color="success" />;
                      }
                      if (active && loading) {
                        return <CircularProgress size={24} />;
                      }
                      return (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            backgroundColor: active ? 'primary.main' : 'grey.300',
                            color: active ? 'white' : 'grey.600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {index + 1}
                        </Box>
                      );
                    }}
                  >
                    <Typography
                      variant={isActive ? 'body1' : 'body2'}
                      color={isActive ? 'primary' : 'text.secondary'}
                      fontWeight={isActive ? 'medium' : 'normal'}
                    >
                      {step}
                    </Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        )}

        {/* Current Message */}
        {message && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" color="text.primary">
              {message}
            </Typography>
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Processing...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Completion Status */}
        {isCompleted && !error && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircle sx={{ mr: 1 }} />
              All steps completed!
            </Box>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {loading ? (
          <Button disabled>
            Processing...
          </Button>
        ) : (
          <>
       {/*      {!isCompleted && (
              <Button onClick={handleCancel} color="inherit">
                Cancel
              </Button>
            )} */}
            <Button onClick={handleClose} variant="contained">
              {isCompleted ? 'Complete' : 'Close'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SignMessageDialog;